import React, { useState, useEffect } from 'react';
import { SetStatistics, Campaign } from '../../types';
import SetIndicator from './SetIndicator';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

interface SetStatisticsPanelProps {
  campaigns: Campaign[];
}

const OBJECTIFS = {
  tauxAcceptation: 95,
  tauxRealisation: 90,
  tauxRefus: 5,
  tauxAnnulation: 3,
  tauxNonRealisation: 5,
};

// ===== Composant SetStatisticsPanel =====
const SetStatisticsPanel: React.FC<SetStatisticsPanelProps> = ({ campaigns }) => {
  const [statistics, setStatistics] = useState<SetStatistics | null>(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [selectedCampaign, setSelectedCampaign] = useState('');

  const fetchStatistics = async () => {
    setLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (selectedCampaign) params.append('campaign', selectedCampaign);

      const response = await fetch(`/api/statistics/set-statistics?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Erreur lors du chargement des statistiques');
      }

      const data = await response.json();

      if (data.success) {
        setStatistics(data.data);
      } else {
        throw new Error(data.error || 'Erreur inconnue');
      }
    } catch (err: any) {
      console.error('Erreur:', err);
      setError(err.message || 'Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const params = new URLSearchParams();
      if (dateFrom) params.append('dateFrom', dateFrom);
      if (dateTo) params.append('dateTo', dateTo);
      if (selectedCampaign) params.append('campaign', selectedCampaign);

      const response = await fetch(`/api/export/statistics-csv?${params.toString()}`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Erreur lors de l\'export');
      }

      const blob = await response.blob();

      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `statistiques-set-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();

      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err: any) {
      console.error('Erreur lors de l\'export CSV:', err);
      alert('Erreur lors de l\'export CSV. Veuillez réessayer.');
    } finally {
      setExporting(false);
    }
  };

  useEffect(() => {
    fetchStatistics();
    }, [dateFrom, dateTo, selectedCampaign]);

  const getVolumeChartData = () => {
    if (!statistics) return [];

    return [
      { name: 'Demandes', value: statistics.nbSetDemandes, fill: '#3b82f6' },
      { name: 'Acceptés', value: statistics.nbSetAcceptes, fill: '#10b981' },
      { name: 'Réalisés', value: statistics.nbSetRealises, fill: '#06b6d4' },
      { name: 'Refusés SR', value: statistics.nbSetRefusesSR, fill: '#ef4444' },
      { name: 'Annulés INFRA', value: statistics.nbSetAnnulesInfra, fill: '#f59e0b' },
      { name: 'Non faits', value: statistics.nbSetNonFaitsSR, fill: '#8b5cf6' },
    ];
  };

  const getTauxChartData = () => {
    if (!statistics) return [];

    return [
      { name: 'Acceptation', valeur: statistics.tauxAcceptation, objectif: OBJECTIFS.tauxAcceptation },
      { name: 'Réalisation', valeur: statistics.tauxRealisation, objectif: OBJECTIFS.tauxRealisation },
      { name: 'Refus', valeur: statistics.tauxRefus, objectif: OBJECTIFS.tauxRefus },
      { name: 'Annulation', valeur: statistics.tauxAnnulation, objectif: OBJECTIFS.tauxAnnulation },
      { name: 'Non-réal.', valeur: statistics.tauxNonRealisation, objectif: OBJECTIFS.tauxNonRealisation },
    ];
  };

  return (
    <div className="space-y-6">
      {/* Filtres */}
      <div className="bg-white p-4 rounded-lg shadow">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">
            <i className="fas fa-filter mr-2 text-blue-600"></i>
            Filtres
          </h3>
          <button
            onClick={handleExportCSV}
            className="btn btn-success"
            disabled={loading || exporting || !statistics}
            title="Exporter les statistiques en CSV"
          >
            {exporting ? (
              <>
                <i className="fas fa-spinner fa-spin mr-2"></i>
                Export en cours...
              </>
            ) : (
              <>
                <i className="fas fa-file-csv mr-2"></i>
                Exporter CSV
              </>
            )}
          </button>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date début
            </label>
            <input
              type="date"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Date fin
            </label>
            <input
              type="date"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              className="form-input w-full"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Campagne
            </label>
            <select
              value={selectedCampaign}
              onChange={(e) => setSelectedCampaign(e.target.value)}
              className="form-input w-full"
            >
              <option value="">Toutes les campagnes</option>
              {campaigns.map((campaign) => (
                <option key={campaign.id} value={campaign.nom}>
                  {campaign.nom}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Indicateur de chargement */}
      {loading && (
        <div className="text-center py-8">
          <i className="fas fa-spinner fa-spin text-4xl text-blue-600"></i>
          <p className="mt-2 text-gray-600">Chargement des statistiques...</p>
        </div>
      )}

      {/* Erreur */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4">
          <i className="fas fa-exclamation-triangle text-red-600 mr-2"></i>
          <span className="text-red-800">{error}</span>
        </div>
      )}

      {/* Statistiques */}
      {!loading && !error && statistics && (
        <>
          {/* 8 Variables principales */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              <i className="fas fa-chart-bar mr-2 text-blue-600"></i>
              Variables principales
            </h3>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="set-stat-card bg-blue-50 border border-blue-200">
                <div className="set-stat-label text-blue-800">Demandes</div>
                <div className="set-stat-value text-blue-900">{statistics.nbSetDemandes}</div>
              </div>
              <div className="set-stat-card bg-green-50 border border-green-200">
                <div className="set-stat-label text-green-800">Acceptés</div>
                <div className="set-stat-value text-green-900">{statistics.nbSetAcceptes}</div>
              </div>
              <div className="set-stat-card bg-cyan-50 border border-cyan-200">
                <div className="set-stat-label text-cyan-800">Réalisés</div>
                <div className="set-stat-value text-cyan-900">{statistics.nbSetRealises}</div>
              </div>
              <div className="set-stat-card bg-red-50 border border-red-200">
                <div className="set-stat-label text-red-800">Refusés SR</div>
                <div className="set-stat-value text-red-900">{statistics.nbSetRefusesSR}</div>
              </div>
              <div className="set-stat-card bg-orange-50 border border-orange-200">
                <div className="set-stat-label text-orange-800">Annulés INFRA</div>
                <div className="set-stat-value text-orange-900">{statistics.nbSetAnnulesInfra}</div>
              </div>
              <div className="set-stat-card bg-purple-50 border border-purple-200">
                <div className="set-stat-label text-purple-800">Non faits</div>
                <div className="set-stat-value text-purple-900">{statistics.nbSetNonFaitsSR}</div>
              </div>
              <div className="set-stat-card bg-gray-50 border border-gray-200">
                <div className="set-stat-label text-gray-800">Attente planif</div>
                <div className="set-stat-value text-gray-900">{statistics.nbSetAttentePlanif}</div>
              </div>
              <div className="set-stat-card bg-yellow-50 border border-yellow-200">
                <div className="set-stat-label text-yellow-800">Attente interv</div>
                <div className="set-stat-value text-yellow-900">{statistics.nbSetAttenteInterv}</div>
              </div>
            </div>
          </div>

          {/* Graphique volumes */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              <i className="fas fa-chart-column mr-2 text-blue-600"></i>
              Répartition des volumes
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getVolumeChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="value" fill="#3b82f6" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* 5 Indicateurs avec objectifs */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              <i className="fas fa-bullseye mr-2 text-blue-600"></i>
              Indicateurs de performance (avec objectifs)
            </h3>
            <div className="space-y-3">
              <SetIndicator
                label="Taux d'acceptation"
                value={statistics.tauxAcceptation}
                target={OBJECTIFS.tauxAcceptation}
                inverse={false}
                tooltip="Pourcentage des demandes acceptées avec date d'intervention planifiée"
              />
              <SetIndicator
                label="Taux de réalisation"
                value={statistics.tauxRealisation}
                target={OBJECTIFS.tauxRealisation}
                inverse={false}
                tooltip="Pourcentage des interventions acceptées qui ont été complètement réalisées"
              />
              <SetIndicator
                label="Taux de refus SR"
                value={statistics.tauxRefus}
                target={OBJECTIFS.tauxRefus}
                inverse={true}
                tooltip="Pourcentage des demandes refusées par le Service Régional"
              />
              <SetIndicator
                label="Taux d'annulation INFRA"
                value={statistics.tauxAnnulation}
                target={OBJECTIFS.tauxAnnulation}
                inverse={true}
                tooltip="Pourcentage des interventions acceptées annulées par l'Infrastructure"
              />
              <SetIndicator
                label="Taux de non-réalisation SR"
                value={statistics.tauxNonRealisation}
                target={OBJECTIFS.tauxNonRealisation}
                inverse={true}
                tooltip="Pourcentage des interventions planifiées qui n'ont pas été réalisées"
              />
            </div>
          </div>

          {/* Graphique des taux vs objectifs */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              <i className="fas fa-chart-line mr-2 text-blue-600"></i>
              Indicateurs vs Objectifs
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={getTauxChartData()}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="name" />
                <YAxis label={{ value: '%', position: 'insideLeft' }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="valeur" fill="#3b82f6" name="Valeur actuelle" />
                <Bar dataKey="objectif" fill="#10b981" name="Objectif" />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Légende des codes couleurs */}
          <div className="bg-white p-6 rounded-lg shadow">
            <h3 className="text-lg font-semibold mb-4">
              <i className="fas fa-palette mr-2 text-blue-600"></i>
              Légende des codes couleurs
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded set-color-green"></div>
                <div>
                  <div className="font-medium">Vert - Objectif atteint</div>
                  <div className="text-sm text-gray-600">Performance conforme aux attentes</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded set-color-yellow"></div>
                <div>
                  <div className="font-medium">Jaune - Vigilance</div>
                  <div className="text-sm text-gray-600">Performance en limite acceptable</div>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded set-color-red"></div>
                <div>
                  <div className="font-medium">Rouge - Alerte</div>
                  <div className="text-sm text-gray-600">Performance en-dessous des attentes</div>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default SetStatisticsPanel;
